import {useCallback} from 'react'
import {ALL_ENTITIES, ExportTabSelection} from '@/features/exports/types'
import {ExportEntityDescriptor, isKnownExportEntity} from '@/features/exports/utils/entities'

const STORAGE_KEY = 'exports:lastTab'

/**
 * Remembers the last-viewed tab in localStorage and resolves the tab to open with
 * (last-viewed if still valid — "all" or a permitted entity — else the first permitted tab) — FR-004.
 */
export const useLastExportTab = (permittedDescriptors: ExportEntityDescriptor[]) => {
    const resolveInitialTab = useCallback((): ExportTabSelection | null => {
        if (permittedDescriptors.length === 0) return null

        const stored = (() => {
            try {
                return localStorage.getItem(STORAGE_KEY)
            } catch {
                return null
            }
        })()

        if (stored === ALL_ENTITIES) return ALL_ENTITIES
        if (stored && isKnownExportEntity(stored) && permittedDescriptors.some(d => d.key === stored)) {
            return stored
        }
        // Default: no entity filter — show exports across all entities.
        return ALL_ENTITIES
    }, [permittedDescriptors])

    const persistTab = useCallback((selection: ExportTabSelection) => {
        try {
            localStorage.setItem(STORAGE_KEY, selection)
        } catch {
            /* ignore persistence errors (private mode / disabled storage) */
        }
    }, [])

    return {resolveInitialTab, persistTab}
}
